import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  Tabs,
  Tab,
  Spinner,
  Badge,
  Modal,
  InputGroup,
} from "react-bootstrap";
import axiosClient from "../api/axiosClient";
import { API_BASE_URL } from "../config/api";

const UserManagement = () => {
  // State for search
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Add state for active subscribers
  const [activeSubscribers, setActiveSubscribers] = useState([]);
  const [loadingSubscribers, setLoadingSubscribers] = useState(false);
  const [showAllSubscribers, setShowAllSubscribers] = useState(false);

  // State for selected user
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [userProfiles, setUserProfiles] = useState([]);
  const [userBoosts, setUserBoosts] = useState([]);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [activeTab, setActiveTab] = useState("details");

  // State for edit modals
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);

  // State for form data
  const [editUserForm, setEditUserForm] = useState({
    email: "",
    fullName: "",
    balance: 0,
    affiliate: "",
  });

  const [editProfileForm, setEditProfileForm] = useState({
    platform: "",
    username: "",
    platform_id: "",
    planName: "",
    interests: "",
    ageRange: "",
    genderTarget: "",
    countries: "",
    followers: 0,
    subscriptionid: null,
  });

  // Handle search
  const handleSearch = async () => {
    if (searchQuery.length < 3) {
      return;
    }

    setSearching(true);
    // Clear the selected user when performing a new search
    setSelectedUser(null);
    // Hide the subscribers view when searching
    setShowAllSubscribers(false);

    try {
      const response = await axiosClient.get(
        `https://api.viralgpt.com/admin/users/search?query=${searchQuery}`,
      );
      setSearchResults(response.data);
    } catch (error) {
      console.error("Error searching users:", error);
    } finally {
      setSearching(false);
    }
  };

  // Handle user selection
  const handleSelectUser = async (userId) => {
    setSelectedUser(userId);
    // Clear out previous user data so stale records are not shown
    setUserDetails(null);
    setUserProfiles([]);
    setUserBoosts([]);
    setUserSubscriptions([]);
    setActiveTab("details");
    await fetchUserDetails(userId);
  };

  // Fetch user details
  const fetchUserDetails = async (userId) => {
    try {
      const response = await axiosClient.get(
        `https://api.viralgpt.com/admin/users/${userId}`,
      );
      setUserDetails(response.data);

      // Pre-fill edit form
      setEditUserForm({
        email: response.data.email || "",
        fullName: response.data.fullName || "",
        balance: response.data.balance || 0,
        affiliate: response.data.affiliate || "",
      });

      // Fetch profiles, boosts, and subscriptions
      fetchUserProfiles(userId);
      fetchUserBoosts(userId);
      fetchUserSubscriptions(userId);
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  // Fetch user profiles with data mapping fixes
  const fetchUserProfiles = async (userId) => {
    try {
      const response = await axiosClient.get(
        `https://api.viralgpt.com/admin/users/${userId}/profiles`,
      );
      // Map the fetched profiles so that the UI gets the expected fields.
      const profiles = response.data.map((profile) => ({
        ...profile,
        // If the backend doesn't return created_at, use timestamp instead.
        created_at: profile.created_at || profile.timestamp,
      }));
      setUserProfiles(profiles);
    } catch (error) {
      console.error("Error fetching user profiles:", error);
    }
  };

  // Fetch user boosts
  const fetchUserBoosts = async (userId) => {
    try {
      const response = await axiosClient.get(
        `https://api.viralgpt.com/admin/users/${userId}/boosts`,
      );
      setUserBoosts(response.data);
    } catch (error) {
      console.error("Error fetching user boosts:", error);
    }
  };

  // Fetch user subscriptions
  const fetchUserSubscriptions = async (userId) => {
    try {
      const response = await axiosClient.get(
        `https://api.viralgpt.com/admin/users/${userId}/subscriptions`,
      );
      setUserSubscriptions(response.data);
    } catch (error) {
      console.error("Error fetching user subscriptions:", error);
    }
  };

  const fetchAllActiveSubscribers = async () => {
    if (showAllSubscribers) {
      // If already showing subscribers, just hide them
      setShowAllSubscribers(false);
      return;
    }

    setLoadingSubscribers(true);
    try {
      const response = await axiosClient.get(
        `https://api.viralgpt.com/admin/users/active-subscribers`,
      );
      setActiveSubscribers(response.data);
      setShowAllSubscribers(true);
      // Clear the selected user when showing all subscribers
      setSelectedUser(null);
    } catch (error) {
      console.error("Error fetching active subscribers:", error);
    } finally {
      setLoadingSubscribers(false);
    }
  };

  // Handle edit user
  const handleEditUser = () => {
    setShowEditUserModal(true);
  };

  // Handle save user changes
  const handleSaveUserChanges = async () => {
    try {
      await axiosClient.put(
        `https://api.viralgpt.com/admin/users/${selectedUser}`,
        editUserForm,
      );
      setShowEditUserModal(false);
      fetchUserDetails(selectedUser);
    } catch (error) {
      console.error("Error updating user:", error);
    }
  };

  // Handle edit profile
  const handleEditProfile = (profile) => {
    setSelectedProfile(profile);
    setEditProfileForm({
      platform: profile.platform || "",
      username: profile.username || "",
      platform_id: profile.platform_id || "",
      planName: profile.planName || "",
      interests: profile.interests || "",
      ageRange: profile.ageRange || "",
      genderTarget: profile.genderTarget || "",
      countries: profile.countries || "",
      followers: profile.followers || 0,
      subscriptionid: profile.subscriptionid || null,
    });
    setShowEditProfileModal(true);
  };

  // Handle save profile changes
  const handleSaveProfileChanges = async () => {
    try {
      await axiosClient.put(
        `https://api.viralgpt.com/admin/users/${selectedUser}/profiles/${selectedProfile.profileid}`,
        editProfileForm,
      );
      setShowEditProfileModal(false);
      fetchUserProfiles(selectedUser);
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };

  // Handle delete profile
  const handleDeleteProfile = async (profileId) => {
    if (window.confirm("Are you sure you want to delete this profile?")) {
      try {
        await axiosClient.delete(
          `https://api.viralgpt.com/admin/users/${selectedUser}/profiles/${profileId}`,
        );
        fetchUserProfiles(selectedUser);
        fetchUserDetails(selectedUser); // Refresh user details to update profile count
      } catch (error) {
        console.error("Error deleting profile:", error);
      }
    }
  };

  // Handle update subscription status
  const handleUpdateSubscriptionStatus = async (subscriptionId, newStatus) => {
    try {
      await axiosClient.put(
        `https://api.viralgpt.com/admin/subscriptions/${subscriptionId}`,
        {
          status: newStatus,
        },
      );
      fetchUserSubscriptions(selectedUser);
    } catch (error) {
      console.error("Error updating subscription status:", error);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Render user search section
  const renderUserSearch = () => (
    <Card className="mb-4">
      <Card.Body>
        <Card.Title>User Search</Card.Title>
        <Form.Group className="mb-3">
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Search by email or ID (min 3 chars)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            />
            <Button
              variant="primary"
              onClick={handleSearch}
              disabled={searchQuery.length < 3 || searching}
            >
              {searching ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                "Search"
              )}
            </Button>
          </InputGroup>
        </Form.Group>

        <div className="d-flex justify-content-end mb-3">
          <Button
            variant={showAllSubscribers ? "secondary" : "success"}
            onClick={fetchAllActiveSubscribers}
            disabled={loadingSubscribers}
            className="text-nowrap"
          >
            {loadingSubscribers ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span className="d-none d-sm-inline ms-2">
                  Loading Subscribers...
                </span>
              </>
            ) : showAllSubscribers ? (
              <>
                <span className="d-none d-sm-inline">Hide</span> Subscribers
              </>
            ) : (
              <>
                <span className="d-none d-sm-inline">Show All</span> Subscribers
              </>
            )}
          </Button>
        </div>

        {showAllSubscribers && activeSubscribers.length > 0 && (
          <>
            <h5>Active Subscribers ({activeSubscribers.length})</h5>
            <div className="table-responsive">
              <Table striped bordered hover className="table-sm">
                <thead>
                  <tr>
                    <th className="d-none d-md-table-cell">ID</th>
                    <th>Email</th>
                    <th>Name</th>
                    <th className="d-none d-md-table-cell">Plan</th>
                    <th className="d-none d-md-table-cell">Next Charge</th>
                    <th>View</th>
                  </tr>
                </thead>
                <tbody>
                  {activeSubscribers.map((user) => (
                    <tr key={user.userid}>
                      <td className="d-none d-md-table-cell">{user.userid}</td>
                      <td
                        className="text-truncate"
                        style={{ maxWidth: "150px" }}
                      >
                        {user.email}
                      </td>
                      <td
                        className="text-truncate"
                        style={{ maxWidth: "100px" }}
                      >
                        {user.fullName}
                      </td>
                      <td className="d-none d-md-table-cell">
                        <Badge bg="success">
                          {user.planName || "Unknown"} ({user.planInterval})
                        </Badge>
                      </td>
                      <td className="d-none d-md-table-cell">
                        {user.nextChargeDate
                          ? formatDate(user.nextChargeDate)
                          : "N/A"}
                      </td>
                      <td>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleSelectUser(user.userid)}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}

        {searchResults.length > 0 && !showAllSubscribers && (
          <div className="table-responsive">
            <Table striped bordered hover className="table-sm">
              <thead>
                <tr>
                  <th className="d-none d-md-table-cell">ID</th>
                  <th>Email</th>
                  <th>Name</th>
                  <th className="d-none d-md-table-cell">Created</th>
                  <th className="d-none d-sm-table-cell">Balance</th>
                  <th>View</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map((user) => (
                  <tr key={user.userid}>
                    <td className="d-none d-md-table-cell">{user.userid}</td>
                    <td className="text-truncate" style={{ maxWidth: "150px" }}>
                      {user.email}
                    </td>
                    <td className="text-truncate" style={{ maxWidth: "100px" }}>
                      {user.fullName}
                    </td>
                    <td className="d-none d-md-table-cell">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="d-none d-sm-table-cell">
                      ${user.balance?.toFixed(2)}
                    </td>
                    <td>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleSelectUser(user.userid)}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );

  // Render user profiles section
  const renderUserProfiles = () => (
    <Card className="mb-4">
      <Card.Body>
        <Card.Title>User Profiles</Card.Title>

        {userProfiles.length === 0 ? (
          <p>No profiles found for this user.</p>
        ) : (
          <div className="table-responsive">
            <Table striped bordered hover className="table-sm">
              <thead>
                <tr>
                  <th className="d-none d-md-table-cell">Profile ID</th>
                  <th>Platform</th>
                  <th>Username</th>
                  <th className="d-none d-sm-table-cell">Plan</th>
                  <th className="d-none d-md-table-cell">Subscription ID</th>
                  <th className="d-none d-md-table-cell">Created</th>
                  <th className="d-none d-sm-table-cell">Followers</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {userProfiles.map((profile) => (
                  <tr key={profile.profileid}>
                    <td className="d-none d-md-table-cell">
                      {profile.profileid}
                    </td>
                    <td>{profile.platform}</td>
                    <td className="text-truncate" style={{ maxWidth: "100px" }}>
                      {profile.username || "Not set"}
                    </td>
                    <td className="d-none d-sm-table-cell">
                      <Badge
                        bg={
                          profile.planName === "pro"
                            ? "success"
                            : profile.planName === "basic"
                              ? "primary"
                              : "secondary"
                        }
                      >
                        {profile.planName || "free"}
                      </Badge>
                    </td>
                    <td className="d-none d-md-table-cell">
                      {profile.subscriptionid || "None"}
                    </td>
                    <td className="d-none d-md-table-cell">
                      {formatDate(profile.created_at || profile.timestamp)}
                    </td>
                    <td className="d-none d-sm-table-cell">
                      {profile.followers || 0}
                    </td>
                    <td>
                      <div className="d-flex flex-nowrap">
                        <Button
                          variant="primary"
                          size="sm"
                          className="me-1"
                          onClick={() => handleEditProfile(profile)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteProfile(profile.profileid)}
                        >
                          Del
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );

  // Render user boosts section
  const renderUserBoosts = () => (
    <Card className="mb-4">
      <Card.Body>
        <Card.Title>Boost Orders</Card.Title>

        {userBoosts.length === 0 ? (
          <p>No boost orders found for this user.</p>
        ) : (
          <div className="table-responsive">
            <Table striped bordered hover className="table-sm">
              <thead>
                <tr>
                  <th className="d-none d-md-table-cell">ID</th>
                  <th>Type</th>
                  <th className="d-none d-sm-table-cell">Name</th>
                  <th>Qty</th>
                  <th className="d-none d-md-table-cell">Price</th>
                  <th>Cost</th>
                  <th>Status</th>
                  <th className="d-none d-md-table-cell">Link</th>
                  <th className="d-none d-md-table-cell">Created</th>
                </tr>
              </thead>
              <tbody>
                {userBoosts.map((boost) => (
                  <tr key={boost.id}>
                    <td className="d-none d-md-table-cell">{boost.id}</td>
                    <td>{boost.boost_type}</td>
                    <td
                      className="d-none d-sm-table-cell text-truncate"
                      style={{ maxWidth: "120px" }}
                    >
                      {boost.boost_name}
                    </td>
                    <td>{boost.quantity}</td>
                    <td className="d-none d-md-table-cell">
                      ${boost.price?.toFixed(2)}
                    </td>
                    <td>${boost.cost?.toFixed(2)}</td>
                    <td>
                      <Badge
                        bg={
                          boost.status === "completed"
                            ? "success"
                            : boost.status === "pending"
                              ? "warning"
                              : boost.status === "failed"
                                ? "danger"
                                : "secondary"
                        }
                      >
                        {boost.status}
                      </Badge>
                    </td>
                    <td className="d-none d-md-table-cell">
                      {boost.link && (
                        <a
                          href={boost.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-truncate d-inline-block"
                          style={{ maxWidth: "100px" }}
                        >
                          {boost.link.length > 20
                            ? `${boost.link.substring(0, 20)}...`
                            : boost.link}
                        </a>
                      )}
                    </td>
                    <td className="d-none d-md-table-cell">
                      {formatDate(boost.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );

  // Render user subscriptions section
  const renderUserSubscriptions = () => (
    <Card className="mb-4">
      <Card.Body>
        <Card.Title>Subscription Orders</Card.Title>

        {userSubscriptions.length === 0 ? (
          <p>No subscription orders found for this user.</p>
        ) : (
          <div className="table-responsive">
            <Table striped bordered hover className="table-sm">
              <thead>
                <tr>
                  <th className="d-none d-md-table-cell">ID</th>
                  <th>Platform</th>
                  <th>Plan</th>
                  <th className="d-none d-sm-table-cell">Interval</th>
                  <th>Status</th>
                  <th className="d-none d-md-table-cell">Start Date</th>
                  <th className="d-none d-md-table-cell">Next Charge</th>
                  <th className="d-none d-sm-table-cell">Price</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {userSubscriptions.map((subscription) => (
                  <tr key={subscription.subscriptionid}>
                    <td className="d-none d-md-table-cell">
                      {subscription.subscriptionid}
                    </td>
                    <td>{subscription.platform}</td>
                    <td>{subscription.planName}</td>
                    <td className="d-none d-sm-table-cell">
                      {subscription.planInterval}
                    </td>
                    <td>
                      <Badge
                        bg={
                          subscription.status === "active"
                            ? "success"
                            : subscription.status === "paused"
                              ? "warning"
                              : subscription.status === "cancelled"
                                ? "danger"
                                : "secondary"
                        }
                      >
                        {subscription.status}
                      </Badge>
                    </td>
                    <td className="d-none d-md-table-cell">
                      {formatDate(subscription.startDate)}
                    </td>
                    <td className="d-none d-md-table-cell">
                      {subscription.nextChargeDate
                        ? formatDate(subscription.nextChargeDate)
                        : "N/A"}
                    </td>
                    <td className="d-none d-sm-table-cell">
                      ${subscription.price?.toFixed(2)}
                    </td>
                    <td>
                      <div className="d-flex flex-nowrap">
                        {subscription.status === "active" && (
                          <>
                            <Button
                              variant="warning"
                              size="sm"
                              className="me-1"
                              onClick={() =>
                                handleUpdateSubscriptionStatus(
                                  subscription.subscriptionid,
                                  "paused",
                                )
                              }
                            >
                              Pause
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() =>
                                handleUpdateSubscriptionStatus(
                                  subscription.subscriptionid,
                                  "cancelled",
                                )
                              }
                            >
                              Cancel
                            </Button>
                          </>
                        )}
                        {subscription.status === "paused" && (
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() =>
                              handleUpdateSubscriptionStatus(
                                subscription.subscriptionid,
                                "active",
                              )
                            }
                          >
                            Resume
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );

  // Render user details section
  const renderUserDetails = () => {
    if (!userDetails) return null;

    return (
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Card.Title>User Details</Card.Title>
            <Button variant="primary" onClick={handleEditUser}>
              Edit User
            </Button>
          </div>

          <Row>
            <Col md={6}>
              <div className="table-responsive">
                <Table bordered className="table-sm">
                  <tbody>
                    <tr>
                      <th>User ID</th>
                      <td>{userDetails.userid}</td>
                    </tr>
                    <tr>
                      <th>Email</th>
                      <td
                        className="text-truncate"
                        style={{ maxWidth: "200px" }}
                      >
                        {userDetails.email}
                      </td>
                    </tr>
                    <tr>
                      <th>Full Name</th>
                      <td>{userDetails.fullName}</td>
                    </tr>
                    <tr>
                      <th>Created</th>
                      <td>{formatDate(userDetails.created_at)}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </Col>
            <Col md={6}>
              <div className="table-responsive">
                <Table bordered className="table-sm">
                  <tbody>
                    <tr>
                      <th>Balance</th>
                      <td>${userDetails.balance?.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <th>IP Address</th>
                      <td>{userDetails.ip}</td>
                    </tr>
                    <tr>
                      <th>Affiliate</th>
                      <td>{userDetails.affiliate || "None"}</td>
                    </tr>
                    <tr>
                      <th>Selected Profile</th>
                      <td>{userDetails.selectedProfile || "None"}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col md={4} className="mb-3 mb-md-0">
              <Card className="text-center">
                <Card.Body>
                  <Card.Title>{userDetails.profileCount}</Card.Title>
                  <Card.Text>Profiles</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-3 mb-md-0">
              <Card className="text-center">
                <Card.Body>
                  <Card.Title>{userDetails.activeSubscriptions}</Card.Title>
                  <Card.Text>Active Subscriptions</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="text-center">
                <Card.Body>
                  <Card.Title>{userDetails.boostCount}</Card.Title>
                  <Card.Text>Boost Orders</Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    );
  };

  // Edit User Modal
  const renderEditUserModal = () => (
    <Modal show={showEditUserModal} onHide={() => setShowEditUserModal(false)}>
      <Modal.Header closeButton>
        <Modal.Title>Edit User</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={editUserForm.email}
              onChange={(e) =>
                setEditUserForm({ ...editUserForm, email: e.target.value })
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Full Name</Form.Label>
            <Form.Control
              type="text"
              value={editUserForm.fullName}
              onChange={(e) =>
                setEditUserForm({ ...editUserForm, fullName: e.target.value })
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Balance</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              value={editUserForm.balance}
              onChange={(e) =>
                setEditUserForm({
                  ...editUserForm,
                  balance: parseFloat(e.target.value),
                })
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Affiliate</Form.Label>
            <Form.Control
              type="text"
              value={editUserForm.affiliate}
              onChange={(e) =>
                setEditUserForm({ ...editUserForm, affiliate: e.target.value })
              }
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowEditUserModal(false)}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSaveUserChanges}>
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
  // Edit Profile Modal
  const renderEditProfileModal = () => (
    <Modal
      show={showEditProfileModal}
      onHide={() => setShowEditProfileModal(false)}
    >
      <Modal.Header closeButton>
        <Modal.Title>Edit Profile</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Platform</Form.Label>
            <Form.Select
              value={editProfileForm.platform}
              onChange={(e) =>
                setEditProfileForm({
                  ...editProfileForm,
                  platform: e.target.value,
                })
              }
            >
              <option value="instagram">Instagram</option>
              <option value="tiktok">TikTok</option>
              <option value="youtube">YouTube</option>
              <option value="twitter">Twitter</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              Username
              {editProfileForm.platform === "youtube" && (
                <small className="text-muted ms-2">
                  [enter @username not channel name]
                </small>
              )}
            </Form.Label>
            <InputGroup>
              <InputGroup.Text>@</InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Enter username"
                value={editProfileForm.username}
                onChange={(e) =>
                  setEditProfileForm({
                    ...editProfileForm,
                    username: e.target.value,
                  })
                }
              />
            </InputGroup>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Plan Name</Form.Label>
            <Form.Select
              value={editProfileForm.planName}
              onChange={(e) =>
                setEditProfileForm({
                  ...editProfileForm,
                  planName: e.target.value,
                })
              }
            >
              <option value="">Free</option>
              <option value="basic">Basic</option>
              <option value="pro">Pro</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Interests</Form.Label>
            <Form.Control
              type="text"
              value={editProfileForm.interests}
              onChange={(e) =>
                setEditProfileForm({
                  ...editProfileForm,
                  interests: e.target.value,
                })
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Age Range</Form.Label>
            <Form.Control
              type="text"
              value={editProfileForm.ageRange}
              onChange={(e) =>
                setEditProfileForm({
                  ...editProfileForm,
                  ageRange: e.target.value,
                })
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Gender Target</Form.Label>
            <Form.Control
              type="text"
              value={editProfileForm.genderTarget}
              onChange={(e) =>
                setEditProfileForm({
                  ...editProfileForm,
                  genderTarget: e.target.value,
                })
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Countries</Form.Label>
            <Form.Control
              type="text"
              value={editProfileForm.countries}
              onChange={(e) =>
                setEditProfileForm({
                  ...editProfileForm,
                  countries: e.target.value,
                })
              }
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={() => setShowEditProfileModal(false)}
        >
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSaveProfileChanges}>
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );

  return (
    <Container fluid>
      {renderUserSearch()}

      {selectedUser && (
        <>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3"
          >
            <Tab eventKey="details" title="User Details">
              {renderUserDetails()}
            </Tab>
            <Tab eventKey="profiles" title="Profiles">
              {renderUserProfiles()}
            </Tab>
            <Tab eventKey="boosts" title="Boost Orders">
              {renderUserBoosts()}
            </Tab>
            <Tab eventKey="subscriptions" title="Subscriptions">
              {renderUserSubscriptions()}
            </Tab>
          </Tabs>

          {renderEditUserModal()}
          {renderEditProfileModal()}
        </>
      )}
    </Container>
  );
};

export default UserManagement;
